import { create } from 'zustand'

import { auth as authApi } from '@/api/endpoints'
import { setAccessToken } from '@/api/client'
import type { User } from '@/types/api'

interface AuthState {
  user: User | null
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous'
  /** Vracia výzvu na kód, keď má účet zapnuté MFA - inak je prihlásený. */
  login: (email: string, password: string, remember?: boolean) => Promise<string | null>
  completeMfa: (challenge: string, code: string) => Promise<void>
  logout: () => Promise<void>
  bootstrap: () => Promise<void>
  setUser: (user: User | null) => void
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'idle',

  setUser: (user) => set({ user, status: user ? 'authenticated' : 'anonymous' }),

  login: async (email, password, remember = false) => {
    const data = await authApi.login(email, password, remember)
    if (data.mfa_required) return data.challenge
    setAccessToken(data.access)
    set({ user: data.user, status: 'authenticated' })
    return null
  },

  completeMfa: async (challenge, code) => {
    const { user, access } = await authApi.loginMfa(challenge, code)
    setAccessToken(access)
    set({ user, status: 'authenticated' })
  },

  logout: async () => {
    try {
      await authApi.logout()
    } finally {
      setAccessToken(null)
      set({ user: null, status: 'anonymous' })
    }
  },

  /** Called once on app start - the httpOnly cookie may already be valid. */
  bootstrap: async () => {
    set({ status: 'loading' })
    try {
      const user = await authApi.me()
      set({ user, status: 'authenticated' })
    } catch {
      set({ user: null, status: 'anonymous' })
    }
  },
}))

// The axios interceptor fires this when a refresh finally fails.
window.addEventListener('l2s:unauthenticated', () => {
  useAuth.setState({ user: null, status: 'anonymous' })
})

export const canWrite = (user: User | null) => Boolean(user?.can_write)
export const canAdministrate = (user: User | null) => Boolean(user?.can_administrate)
